"use client";
import * as React from "react";
import {
  Box,
  Card,
  Flex,
  Inset,
  Separator,
  Strong,
  Text,
} from "@radix-ui/themes";
import { Avatar, Button, Skeleton } from "./elements.js";
import * as DataList from "./data-list.js";
import { Me, OAuthProfile, MeOauthProfiles } from "../api/endpoint.js";
import { ChangeEmailDialog } from "./change-email-dialog.js";
import { EditUserProfileDialog } from "./edit-user-profile-dialog.js";
import {
  getBestName,
  getDomProps,
  type WidgetRootState,
  type WidgetRootDomProps,
} from "./utils.js";
import { OAuthIcon, getOAuthName, type OAuthAccount } from "./oauth-icons.js";
import { GenericError } from "./generic-error.js";
import { Translation } from "./i18n/translation.js";

interface UserProfileProps extends WidgetRootDomProps {
  userData: Me;
}

const UserProfile: React.FC<UserProfileProps> = ({
  userData: user,
  ...domProps
}) => {
  const oauthAccounts = user.oauthProfiles
    ? (Object.entries(user.oauthProfiles) as [
        keyof Exclude<MeOauthProfiles, null>,
        OAuthProfile,
      ][])
    : [];
  return (
    <Card size="2" {...getWidgetRootDomProps("resolved", domProps)}>
      <List>
        {user.profilePictureUrl && (
          <>
            <ListItem>
              <ListTitle minWidth="220px">
                <Translation
                  defaultMessage="Profile picture"
                  description="Label for the user's avatar"
                  id="RvLqCQ"
                />
              </ListTitle>
              <ListValue>
                <Avatar
                  size="2"
                  fallback={<FallbackUserIcon />}
                  src={user.profilePictureUrl}
                />
              </ListValue>
            </ListItem>

            <ListSeparator />
          </>
        )}

        <ListItem>
          <ListTitle>
            <Translation
              defaultMessage="Full name"
              id="kkBScm"
              description="Label for user's full name field"
            />
          </ListTitle>
          <ListValue>
            <Flex
              align="center"
              justify="between"
              width="100%"
              minHeight="var(--space-6)"
            >
              <Text size="2">
                {getBestName(user) || (
                  <Text
                    color="gray"
                    style={{ color: "var(--gray-10)", cursor: "default" }}
                  >
                    <Translation
                      defaultMessage="Not set"
                      id="i4vzAY"
                      description="Placeholder text when user's name is not set"
                    />
                  </Text>
                )}
              </Text>

              <EditUserProfileDialog user={user}>
                <Button variant="secondary">
                  <Translation
                    defaultMessage="Edit"
                    id="5bhXUz"
                    description="Button text to edit user profile"
                  />
                </Button>
              </EditUserProfileDialog>
            </Flex>
          </ListValue>
        </ListItem>

        <ListSeparator />

        <ListItem>
          <ListTitle>
            <Translation
              defaultMessage="Email address"
              id="nLk5pa"
              description="Label for user's email address field"
            />
          </ListTitle>
          <ListValue>
            <Flex
              align="center"
              justify="between"
              width="100%"
              minHeight="var(--space-6)"
            >
              <Text size="2">{user.email}</Text>

              <ChangeEmailDialog user={user}>
                <Button variant="secondary">
                  <Translation
                    defaultMessage="Edit"
                    id="+2ILQR"
                    description="Button text to edit the user's email address"
                  />
                </Button>
              </ChangeEmailDialog>
            </Flex>
          </ListValue>
        </ListItem>

        {oauthAccounts.length > 0 && (
          <>
            <ListSeparator />

            <ListItem>
              <ListTitle>
                <Translation
                  defaultMessage="Connected accounts"
                  id="AW/A69"
                  description="Label for user's connected OAuth accounts"
                />
              </ListTitle>
              <ListValue>
                <Flex direction="column" gap="2">
                  {oauthAccounts.map(([account, data]) => (
                    <OAuthAccount
                      key={account}
                      account={account}
                      email={data?.email}
                    />
                  ))}
                </Flex>
              </ListValue>
            </ListItem>
          </>
        )}
      </List>
    </Card>
  );
};

interface UserProfileLoadingProps extends WidgetRootDomProps {}

const UserProfileLoading: React.FC<UserProfileLoadingProps> = (props) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("loading", props)}>
      <List>
        <ListItem>
          <ListTitle minWidth="220px">
            <Skeleton>
              <Translation
                defaultMessage="Full name"
                id="nc7fru"
                description="Loading placeholder for full name label"
              />
            </Skeleton>
          </ListTitle>
          <ListValue>
            <Flex
              align="center"
              justify="between"
              width="100%"
              minHeight="var(--space-6)"
            >
              <Text size="2">
                <Skeleton>
                  <Translation
                    defaultMessage="Full name"
                    id="6GcWa2"
                    description="Loading placeholder for full name value"
                  />
                </Skeleton>
              </Text>
            </Flex>
          </ListValue>
        </ListItem>

        <ListSeparator />

        <ListItem>
          <ListTitle>
            <Skeleton>
              <Translation
                defaultMessage="Email address"
                id="gjqw+H"
                description="Loading placeholder for email address label"
              />
            </Skeleton>
          </ListTitle>
          <ListValue>
            <Flex align="center" minHeight="var(--space-6)">
              <Text size="2">
                <Skeleton>
                  <Translation
                    defaultMessage="Email address"
                    id="75F+g5"
                    description="Loading placeholder for email address value"
                  />
                </Skeleton>
              </Text>
            </Flex>
          </ListValue>
        </ListItem>
      </List>
    </Card>
  );
};

interface UserProfileErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const UserProfileError: React.FC<UserProfileErrorProps> = ({
  error,
  ...domProps
}) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <GenericError error={error} />
    </Card>
  );
};

const ListIdContext = React.createContext<string | null>(null);
ListIdContext.displayName = "ListIdContext";

const List = ({ children }: { children: React.ReactNode }) => {
  const listId = `user-profile-list-${React.useId()}`;
  return (
    <ListIdContext.Provider value={listId}>
      <DataList.Root as="div">{children}</DataList.Root>
    </ListIdContext.Provider>
  );
};

const ListItemIdContext = React.createContext<string | null>(null);
ListItemIdContext.displayName = "ListItemIdContext";

const ListItem = ({ children }: { children: React.ReactNode }) => {
  const listId = React.useContext(ListIdContext);
  const currentId = React.useId();
  const listItemId = [listId, "item", currentId].filter(Boolean).join("-");
  return (
    <ListItemIdContext.Provider value={listItemId}>
      <DataList.Item align="center">{children}</DataList.Item>
    </ListItemIdContext.Provider>
  );
};

const ListTitle = ({
  children,
  ...props
}: Omit<DataList.LabelProps, "as" | "role" | "asChild">) => {
  const listItemId = React.useContext(ListItemIdContext);
  const titleId = [listItemId, "title"].filter(Boolean).join("-");
  return (
    <DataList.Label as="div" role="term" highContrast id={titleId} {...props}>
      <Strong>{children}</Strong>
    </DataList.Label>
  );
};

const ListValue = ({ children }: { children: React.ReactNode }) => {
  const listItemId = React.useContext(ListItemIdContext);
  const titleId = [listItemId, "title"].filter(Boolean).join("-");
  return (
    <DataList.Value as="div" role="definition" aria-labelledby={titleId}>
      {children}
    </DataList.Value>
  );
};

const ListSeparator = () => (
  <Box asChild gridColumn="span 2" aria-hidden>
    <Inset side="x">
      <Separator size="4" />
    </Inset>
  </Box>
);

const OAuthAccount = ({
  account,
  email,
}: {
  account: OAuthAccount;
  email?: string | null;
}) => {
  const name = getOAuthName(account);
  return (
    <Flex align="center" gap="1">
      <OAuthIcon account={account} />
      <Text size="2" ml="1">
        {name}
      </Text>
      {email && (
        <Box display={{ initial: "none", sm: "contents" }}>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <Text size="2" color="gray">
            ∙
          </Text>
          <Text size="2" color="gray">
            {email}
          </Text>
        </Box>
      )}
    </Flex>
  );
};

const FallbackUserIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 256 256"
  >
    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
    <title>User icon</title>
    <path d="M229.19,213c-15.81-27.32-40.63-46.49-69.47-54.62a70,70,0,1,0-63.44,0C67.44,166.5,42.62,185.67,26.81,213a6,6,0,1,0,10.38,6C56.4,185.81,90.34,166,128,166s71.6,19.81,90.81,53a6,6,0,1,0,10.38-6ZM70,96a58,58,0,1,1,58,58A58.07,58.07,0,0,1,70,96Z" />
  </svg>
);

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "user-profile",
    widgetState: state,
  });
}

export type {
  UserProfileProps,
  UserProfileLoadingProps,
  UserProfileErrorProps,
};
export { UserProfile, UserProfileLoading, UserProfileError };
